import { observer } from 'mobx-react-lite';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from '@shared/utils/i18n';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { OsiActsWidgetViewModel } from '@widgets/osiActs/model/viewModel';
import { PdfViewer } from '@shared/ui/pdfViewer/ui';

interface Props {
  viewModel: OsiActsWidgetViewModel;
}

export const SignedActsDialog: React.FC<Props> = observer(({ viewModel }) => {
  const {
    signedActsDialog,
    onCloseSignedActsDialog,
    onPrintAct,
    onDownloadAct,
    onConfirmDownloadPrintForm,
    onCancelDownloadPrintForm
  } = viewModel;
  const { t } = useTranslation();

  // OSI-818: акт подписан в составе общего акта по БИН/ИИН — меняются заголовок и подсказка:
  // в просмотре и в «Сохранить» общий документ по всем домам, а «Печать» — акт этого дома.
  const isCombined = Boolean(signedActsDialog.act?.combinedActId);
  const combinedIdn = viewModel.signedDialogCombinedAct?.idn || signedActsDialog.act?.osiIdn || '';

  return (
    <>
      {/* OSI-818: fullWidth вместо minWidth 1200 у содержимого — PdfViewer подстраивает
          страницы под ширину диалога, горизонтальной прокрутки нет. */}
      <Dialog open={viewModel.signedActsDialog.isOpen} onClose={onCloseSignedActsDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isCombined
            ? t('acts:combinedAct.signedDialogTitle', { actNum: signedActsDialog.act?.actNum, idn: combinedIdn })
            : signedActsDialog.act?.actNum}
        </DialogTitle>
        <DialogContent>
          <Box>
            {/* OSI-818: у акта из общего акта в просмотре общий документ по всем домам —
                при открытии прокручиваем к акту этого дома (поиск по номеру акта). */}
            {signedActsDialog.doc?.base64 && (
              <PdfViewer
                base64={signedActsDialog.doc.base64}
                scrollToText={isCombined ? signedActsDialog.act?.actNum : undefined}
                scrollToLabel={t('acts:combinedAct.scrollToHouseAct')}
              />
            )}
          </Box>
          {/* OSI-763: в просмотре обычно сам подписанный документ — подсказываем, что
              расшифровка ЭЦП есть только в печатной форме, по кнопке «Печать».
              Если подписанного файла нет (старые акты), показана печатная форма —
              и текст другой: предупреждаем, что документ на экране без ЭЦП. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t(signedActsDialog.isPrintFormShown ? 'acts:signedActs.noOriginalHint' : 'acts:signedActs.formHint')}
          </Typography>
          {/* OSI-818: подсказка для акта из общего акта — что именно отдаёт каждая кнопка. */}
          {isCombined && !signedActsDialog.isPrintFormShown && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('acts:combinedAct.signedHint')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={onPrintAct}>
            {t('common:print')}
          </Button>
          {/* OSI-763: «Сохранить» отдаёт подписанный файл из скана, а не печатную форму.
              Скана может не быть (старые акты) — кнопка активна всегда, но тогда по клику
              открывается подтверждение с предложением скачать печатную форму без ЭЦП. */}
          <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={onDownloadAct}>
            {t('common:save')}
          </Button>
          <Button variant="outlined" color="inherit" onClick={onCloseSignedActsDialog}>
            {t('common:close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OSI-763: подтверждение для актов без подписанного файла. Отдать печатную форму
          молча нельзя — она без ЭЦП и юридической силы не имеет, пользователь должен
          согласиться осознанно. Открывается только из onDownloadAct при scanId === null. */}
      <Dialog open={signedActsDialog.isNoScanConfirmOpen} onClose={onCancelDownloadPrintForm} maxWidth="xs">
        <DialogTitle>{t('acts:signedActs.noScanConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('acts:signedActs.noScanConfirm.text')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={onConfirmDownloadPrintForm}>
            {t('acts:signedActs.noScanConfirm.confirm')}
          </Button>
          <Button variant="outlined" color="inherit" onClick={onCancelDownloadPrintForm}>
            {t('common:cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
